/**
 * CommandModule — authenticated app shell: header/nav, workspace rail, section routes, onboarding and settings modals.
 * Composition: command/useUserState (state machine + hooks), command/AppHeader, command/AppRoutes.
 */
import { useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import SettingsModal from "@/components/SettingsModal";
import ProfileCompletionPrompt from "@/components/ProfileCompletionPrompt";
import { useUserState, useKeepAlive, useActiveInstance } from "./command/useUserState";
import { AppHeader, LoadingScreen } from "./command/AppHeader";
import { AppRoutes, WorkspacePanel } from "./command/AppRoutes";

const AUTH_KEYS = ["isAuthenticated", "username", "userId", "authToken", "refreshToken"];

const CommandModule = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserState(userId, navigate);
  useKeepAlive();

  const [showSettings, setShowSettings] = useState(false);
  const [showInstanceSwitcher, setShowInstanceSwitcher] = useState(false);
  const [currentInstance, setCurrentInstance] = useActiveInstance(userId, !user.isLoading && !user.isNewUser);

  // Autonomous stream state (persists across navigation)
  const [streamUpdates, setStreamUpdates] = useState([]);
  const [streamPaused, setStreamPaused] = useState(false);

  const handleLogout = useCallback(() => {
    AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
    navigate("/");
  }, [navigate]);

  const { fetchUserProfile } = user;
  const handleSettingsClose = useCallback(() => {
    setShowSettings(false);
    fetchUserProfile();
  }, [fetchUserProfile]);

  if (user.isLoading) return <LoadingScreen />;

  const isLoggedInRoute = !user.isNewUser && userId && userId !== "new";

  return (
    <div className="min-h-screen gradient-bg relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-30" />
      </div>

      <AppHeader userId={userId} currentPath={location.pathname.split("/").pop() || ""} isNewUser={user.isNewUser} currentInstance={currentInstance}
        showInstanceSwitcher={showInstanceSwitcher} onToggleInstanceSwitcher={() => setShowInstanceSwitcher((v) => !v)}
        onSetup={user.isNewUser ? user.openOnboarding : () => setShowSettings(true)} onLogout={handleLogout} navigate={navigate} />

      {isLoggedInRoute && <ProfileCompletionPrompt userId={userId} />}

      <div className="flex">
        <WorkspacePanel open={showInstanceSwitcher} userId={userId} currentInstance={currentInstance} onInstanceChange={setCurrentInstance} />
        <main className="container mx-auto px-6 py-8 relative z-10 flex-1">
          <AppRoutes userId={userId} currentInstance={currentInstance} onOpenTargeting={() => setShowSettings(true)}
            stream={{ streamUpdates, setStreamUpdates, streamPaused, setStreamPaused }} />
        </main>
      </div>

      <footer className="relative z-10 text-center py-8">
        <p className="text-xs text-gray-600">
          © Copyright 2026 <span className="text-gray-500">Intelligent Sales Machines, Inc.</span> – All rights reserved.
        </p>
      </footer>

      {user.shouldShowOnboarding && (
        <OnboardingFlow userId={userId === "new" ? localStorage.getItem("userId") : userId} onClose={user.closeOnboarding}
          closable={userId !== "new"} onComplete={user.completeOnboarding} />
      )}

      <SettingsModal isOpen={showSettings} onClose={handleSettingsClose} userId={userId} initialData={user.userData} />
    </div>
  );
};

export default CommandModule;
